import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatINR } from "@/lib/api";
import {
    LayoutDashboard,
    Calendar,
    MessageSquare,
    Mail,
    CreditCard,
    LogOut,
    Download,
    Check,
    X,
    FileText,
    Plus,
    Trash2,
    Sparkles,
    Stars,
    Send,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { id: "overview",  label: "Overview",  icon: LayoutDashboard },
    { id: "bookings",  label: "Bookings",  icon: Calendar },
    { id: "messages",  label: "Messages",  icon: MessageSquare },
    { id: "newsletter",label: "Newsletter",icon: Mail },
    { id: "payments",  label: "Payments",  icon: CreditCard },
    { id: "blogs",     label: "Blogs",     icon: FileText },
    { id: "marketing", label: "Marketing", icon: Sparkles },
];

export default function AdminDashboard() {
    const { admin, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        if (!loading && !admin) navigate("/admin/login", { replace: true });
    }, [loading, admin, navigate]);

    if (loading) return <div className="pt-32 container-vv text-cream-muted">Loading...</div>;
    if (!admin) return null;

    return (
        <div data-testid="admin-dashboard" className="pt-24 pb-20 min-h-screen">
            <div className="container-vv">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar */}
                    <aside className="lg:w-64 flex-shrink-0">
                        <div className="card-luxe sticky top-28 p-5">
                            <div className="mb-6">
                                <div className="label-eyebrow">Logged in as</div>
                                <div className="font-serif text-lg text-cream mt-1">{admin.email}</div>
                            </div>
                            <nav className="flex lg:flex-col gap-1 flex-wrap">
                                {TABS.map((t) => {
                                    const Icon = t.icon;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setTab(t.id)}
                                            data-testid={`tab-${t.id}`}
                                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm w-full text-left transition-colors ${
                                                tab === t.id ? "bg-gold text-ink" : "text-cream hover:bg-gold/10"
                                            }`}
                                        >
                                            <Icon className="h-4 w-4" /> {t.label}
                                        </button>
                                    );
                                })}
                            </nav>
                            <button onClick={() => { logout(); navigate("/"); }} className="mt-6 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-destructive hover:bg-destructive/10" data-testid="admin-logout">
                                <LogOut className="h-4 w-4" /> Sign out
                            </button>
                        </div>
                    </aside>

                    <main className="flex-1 min-w-0">
                        {tab === "overview" && <Overview />}
                        {tab === "bookings" && <Bookings />}
                        {tab === "messages" && <Messages />}
                        {tab === "newsletter" && <Newsletter />}
                        {tab === "payments" && <Payments />}
                        {tab === "blogs" && <Blogs />}
                        {tab === "marketing" && <Marketing />}
                    </main>
                </div>
            </div>
        </div>
    );
}

const Overview = () => {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
    if (!stats) return <div className="text-cream-muted">Loading stats...</div>;
    const cards = [
        { label: "Total Bookings", value: stats.total_bookings },
        { label: "Confirmed", value: stats.confirmed },
        { label: "Pending", value: stats.pending },
        { label: "Paid Sessions", value: stats.paid },
        { label: "Revenue", value: formatINR(stats.revenue_inr) },
        { label: "Newsletter Subs", value: stats.newsletter_subscribers },
        { label: "Contact Messages", value: stats.contact_messages },
        { label: "Cancelled", value: stats.cancelled },
    ];
    return (
        <div data-testid="overview-panel">
            <h1 className="font-serif text-3xl text-cream mb-2">Dashboard</h1>
            <p className="text-cream-muted text-sm mb-8">Snapshot of your practice</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {cards.map((c) => (
                    <div key={c.label} className="card-luxe p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                        <div className="text-xs tracking-widest uppercase text-cream-muted">{c.label}</div>
                        <div className="font-serif text-2xl text-cream mt-2">{c.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const exportToCsv = (rows, name) => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${(r[k] ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${name}.csv`; a.click(); URL.revokeObjectURL(url);
};

const Bookings = () => {
    const [items, setItems] = useState([]);
    const load = () => api.get("/admin/bookings").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const setStatus = async (id, status) => {
        await api.patch(`/admin/bookings/${id}`, { status });
        toast.success(`Marked ${status}`);
        load();
    };
    return (
        <div data-testid="bookings-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-serif text-3xl text-cream">Bookings</h1>
                    <p className="text-cream-muted text-sm">{items.length} total</p>
                </div>
                <button onClick={() => exportToCsv(items, "bookings")} className="btn-outline-gold text-sm" data-testid="export-bookings-btn"><Download className="h-4 w-4" /> Export CSV</button>
            </div>
            <div className="card-luxe p-0 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink-deep/60 text-left">
                        <tr className="text-cream-muted text-[11px] tracking-widest uppercase">
                            <th className="p-4">When</th>
                            <th className="p-4">Client</th>
                            <th className="p-4">Service</th>
                            <th className="p-4">Amount</th>
                            <th className="p-4">Pay</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((b) => (
                            <tr key={b.id} className="border-t border-gold/10 hover:bg-ink-deep/40" data-testid={`booking-row-${b.id}`}>
                                <td className="p-4 text-cream">{b.date} <span className="text-cream-muted">{b.time_slot}</span></td>
                                <td className="p-4 text-cream">{b.name}<br /><span className="text-xs text-cream-muted">{b.email}</span></td>
                                <td className="p-4 text-cream">{b.service_name}</td>
                                <td className="p-4 text-gold font-serif">{formatINR(b.price_inr)}</td>
                                <td className="p-4">
                                    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded-full ${b.payment_status === "paid" ? "bg-gold/20 text-gold" : "bg-cream-muted/10 text-cream-muted"}`}>{b.payment_status}</span>
                                </td>
                                <td className="p-4">
                                    <span className="text-[10px] tracking-widest uppercase">{b.status}</span>
                                </td>
                                <td className="p-4">
                                    <div className="flex gap-2">
                                        <button onClick={() => setStatus(b.id, "confirmed")} className="text-gold hover:text-gold-light" data-testid={`accept-${b.id}`}><Check className="h-4 w-4" /></button>
                                        <button onClick={() => setStatus(b.id, "cancelled")} className="text-destructive hover:opacity-80" data-testid={`reject-${b.id}`}><X className="h-4 w-4" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="p-8 text-center text-cream-muted">No bookings yet</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Messages = () => {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/contact-messages").then((r) => setItems(r.data)); }, []);
    return (
        <div data-testid="messages-panel">
            <h1 className="font-serif text-3xl text-cream mb-6">Contact Messages</h1>
            <div className="space-y-4">
                {items.map((m) => (
                    <div key={m.id} className="card-luxe">
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <div className="font-serif text-lg text-cream">{m.name}</div>
                                <div className="text-xs text-cream-muted">{m.email} · {m.phone || "no phone"}</div>
                            </div>
                            <div className="text-xs text-cream-muted">{m.created_at}</div>
                        </div>
                        {m.subject && <div className="text-sm text-gold mb-1">{m.subject}</div>}
                        <p className="text-cream text-sm">{m.message}</p>
                    </div>
                ))}
                {items.length === 0 && <div className="text-cream-muted text-center py-8">No messages yet</div>}
            </div>
        </div>
    );
};

const Newsletter = () => {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/newsletter").then((r) => setItems(r.data)); }, []);
    return (
        <div data-testid="newsletter-panel">
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif text-3xl text-cream">Newsletter Subscribers</h1>
                <button onClick={() => exportToCsv(items, "newsletter")} className="btn-outline-gold text-sm"><Download className="h-4 w-4" /> Export CSV</button>
            </div>
            <div className="card-luxe p-0 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-ink-deep/60 text-left text-cream-muted text-[11px] tracking-widest uppercase">
                        <tr><th className="p-4">Email</th><th className="p-4">Subscribed</th></tr>
                    </thead>
                    <tbody>
                        {items.map((s, i) => (
                            <tr key={i} className="border-t border-gold/10">
                                <td className="p-4 text-cream">{s.email}</td>
                                <td className="p-4 text-cream-muted text-xs">{s.subscribed_at}</td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={2} className="p-8 text-center text-cream-muted">No subscribers</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Payments = () => {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/payments").then((r) => setItems(r.data)); }, []);
    return (
        <div data-testid="payments-panel">
            <h1 className="font-serif text-3xl text-cream mb-6">Payments</h1>
            <div className="card-luxe p-0 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink-deep/60 text-cream-muted text-left text-[11px] tracking-widest uppercase">
                        <tr><th className="p-4">Session</th><th className="p-4">Booking</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Created</th></tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.session_id} className="border-t border-gold/10">
                                <td className="p-4 text-cream font-mono text-xs">{p.session_id.slice(0, 18)}...</td>
                                <td className="p-4 text-cream font-mono text-xs">{p.booking_id?.slice(0, 8) || "—"}</td>
                                <td className="p-4 text-gold font-serif">{formatINR((p.amount || 0) / 100)}</td>
                                <td className="p-4"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${p.payment_status === "paid" ? "bg-gold/20 text-gold" : "bg-cream-muted/10 text-cream-muted"}`}>{p.payment_status}</span></td>
                                <td className="p-4 text-cream-muted text-xs">{p.created_at}</td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-cream-muted">No payments yet</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Blogs = () => {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: "", slug: "", category: "Vedic Astrology", excerpt: "", content: "", cover_url: "", published: true });
    const load = () => api.get("/admin/blogs").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/blogs", form);
            toast.success("Blog created");
            setShowForm(false);
            setForm({ title: "", slug: "", category: "Vedic Astrology", excerpt: "", content: "", cover_url: "", published: true });
            load();
        } catch (err) { toast.error("Failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this blog?")) return;
        await api.delete(`/admin/blogs/${id}`);
        load();
    };

    return (
        <div data-testid="blogs-panel">
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif text-3xl text-cream">Blogs</h1>
                <button onClick={() => setShowForm((v) => !v)} className="btn-gold text-sm" data-testid="new-blog-btn"><Plus className="h-4 w-4" /> New post</button>
            </div>

            {showForm && (
                <form onSubmit={submit} className="card-luxe mb-6 space-y-3" data-testid="blog-form">
                    <div className="grid md:grid-cols-2 gap-3">
                        <input className="input-luxe" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} />
                        <input className="input-luxe" required placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                        <select className="input-luxe" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                            {["Vedic Astrology", "Numerology", "Planetary Transits", "Horoscope", "Remedies", "Panchang", "Nakshatras"].map((c) => (
                                <option key={c} value={c} className="bg-ink">{c}</option>
                            ))}
                        </select>
                        <input className="input-luxe" placeholder="Cover image URL" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} />
                    </div>
                    <input className="input-luxe" required placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
                    <textarea className="input-luxe" rows={6} required placeholder="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                    <button type="submit" className="btn-gold text-sm">Publish</button>
                </form>
            )}

            <div className="space-y-3">
                {items.map((b) => (
                    <div key={b.id} className="card-luxe flex items-start justify-between gap-4" data-testid={`admin-blog-${b.slug}`}>
                        <div>
                            <div className="label-eyebrow">{b.category}</div>
                            <div className="font-serif text-lg text-cream mt-1">{b.title}</div>
                            <div className="text-xs text-cream-muted mt-1">/{b.slug} · {b.created_at}</div>
                        </div>
                        <button onClick={() => remove(b.id)} className="text-destructive p-2"><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
                {items.length === 0 && <div className="text-cream-muted text-center py-8">No custom blogs yet. Default posts are shown to visitors. Add your own using the button above.</div>}
            </div>
        </div>
    );
};


const Marketing = () => {
    const [blasting, setBlasting] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [subCount, setSubCount] = useState(null);
    const [leadCount, setLeadCount] = useState(null);

    useEffect(() => {
        // Try to fetch counts (best-effort — endpoints may not exist yet)
        api.get("/admin/newsletter").then((r) => setSubCount(r.data?.length)).catch(() => {});
    }, []);

    const blast = async () => {
        if (!window.confirm("Send today's horoscope to ALL subscribers right now?")) return;
        setBlasting(true);
        setLastResult(null);
        try {
            const { data } = await api.post("/admin/daily-horoscope/blast");
            setLastResult(data);
            toast.success(`Horoscope sent to ${data.sent} subscribers`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Blast failed");
        } finally {
            setBlasting(false);
        }
    };

    return (
        <div data-testid="marketing-panel">
            <h1 className="font-serif text-3xl text-cream mb-2">Marketing</h1>
            <p className="text-cream-muted text-sm mb-8">Growth tools — horoscope blasts, leads, SEO</p>

            {/* Daily Horoscope Blast */}
            <div className="card-luxe mb-6">
                <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center flex-shrink-0">
                        <Stars className="h-6 w-6 text-gold" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-serif text-xl text-cream">Daily Horoscope Blast</h2>
                        <p className="text-sm text-cream-muted mt-1 leading-relaxed">
                            Auto-runs at <strong className="text-gold">06:00 IST every day</strong> via server cron — sends today's Vedic reading to all subscribers based on their rashi.
                        </p>
                        <p className="text-xs text-cream-dim mt-2">Use the button below to send manually right now (e.g. testing or off-schedule blast).</p>
                        <button
                            onClick={blast}
                            disabled={blasting}
                            className="btn-gold text-sm mt-4 disabled:opacity-50"
                            data-testid="horoscope-blast-btn"
                        >
                            {blasting ? "Sending..." : <><Send className="h-4 w-4" /> Send Today's Horoscope Now</>}
                        </button>
                        {lastResult && (
                            <div className="mt-4 rounded-xl border border-gold/30 bg-ink-deep/60 p-3 text-sm text-cream" data-testid="blast-result">
                                ✓ Sent <strong className="text-gold">{lastResult.sent}</strong> emails for <strong>{lastResult.date}</strong>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="card-luxe p-5">
                    <div className="text-xs tracking-widest uppercase text-cream-muted">Newsletter Subs</div>
                    <div className="font-serif text-3xl text-gold mt-2">{subCount ?? "—"}</div>
                </div>
                <div className="card-luxe p-5">
                    <div className="text-xs tracking-widest uppercase text-cream-muted">Cron Schedule</div>
                    <div className="font-serif text-3xl text-cream mt-2">06:00 IST</div>
                    <div className="text-xs text-cream-muted mt-1">daily horoscope blast</div>
                </div>
            </div>

            {/* SEO tools */}
            <div className="card-luxe">
                <h2 className="font-serif text-xl text-cream mb-3">SEO & Discoverability</h2>
                <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-cream">
                        <span className="text-gold">✓</span>
                        <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="hover:text-gold underline-offset-2 hover:underline">View Sitemap.xml</a>
                    </li>
                    <li className="flex items-center gap-2 text-cream">
                        <span className="text-gold">✓</span>
                        <a href="/api/robots.txt" target="_blank" rel="noreferrer" className="hover:text-gold underline-offset-2 hover:underline">View Robots.txt</a>
                    </li>
                    <li className="flex items-center gap-2 text-cream-muted">
                        <span className="text-gold">→</span>
                        Submit sitemap to <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-gold hover:underline">Google Search Console</a>
                    </li>
                </ul>
            </div>
        </div>
    );
};
